import type { SpellCorrection } from '@/redux/modules/search/search.types';
import type { SpellCorrectionAnalytics } from '@/utils/amplitude/analytics/search/trackSearchCompleted.analytics';

import { INITIAL_FACET_OVERRIDES } from '@/pages/Search/SearchPage/types';
import { SearchExclusionSlice } from '@/redux/modules/search/exclusions/searchExclusions.types';
import { SearchUrlTrigger } from '@/types/search/enums/searchUrlTrigger';
import {
    AugmentedSearchQuery,
    AugmentedSearchQueryOption,
    AugmentedSearchQueryOptions,
    SearchQuery,
} from '@/types/search/searchQuery';
import { SearchCompletedTrigger } from '@/utils/amplitude/analytics/core.types';

const emptyAuctionHouseFacet: AugmentedSearchQueryOption = { exclude: [], include: [] };

/**
 * Build the `options` block search-party expects: each facet with user-defined exclusions becomes
 * `[{ exclude, include }]`, and any page-level auction house exclusions are unioned onto
 * `auctionHouse`.
 *
 * Shared by the organic search path and `searchPromoted`, which used to carry its own copy of this
 * logic -- so a sponsored lot from an excluded house could still surface on a page that excluded
 * that house.
 */
export const getAugmentedSearchOptions = (
    options: SearchQuery['options'],
    searchExclusions: SearchExclusionSlice['excludedFacets'],
    pageExcludedAuctionHouseIds: string[] = []
): AugmentedSearchQueryOptions => {
    const augmentedOptions: AugmentedSearchQueryOptions = Object.keys(searchExclusions).reduce(
        (acc, facetWithExclusions) => ({
            ...acc,
            [facetWithExclusions]: [
                {
                    exclude: searchExclusions[facetWithExclusions] || [],
                    include: options?.[facetWithExclusions] || [],
                },
            ],
        }),
        {
            ...options,
            auctionHouse: [emptyAuctionHouseFacet],
        }
    );

    if (pageExcludedAuctionHouseIds.length) {
        const [houseFacet] = augmentedOptions.auctionHouse ?? [emptyAuctionHouseFacet];
        augmentedOptions.auctionHouse = [
            {
                ...houseFacet,
                exclude: Array.from(new Set([...houseFacet.exclude, ...pageExcludedAuctionHouseIds])),
            },
        ];
    }

    return augmentedOptions;
};

export const getAugmentedSearchQuery = (
    searchQuery: SearchQuery,
    searchExclusions: SearchExclusionSlice['excludedFacets']
): AugmentedSearchQuery => {
    // `excludedAuctionHouseIds` is a frontend-only field; it is folded into `options` and must
    // never reach the wire.
    const { excludedAuctionHouseIds, ...queryForRequest } = searchQuery;

    const augmentedSearchQuery: AugmentedSearchQuery = {
        ...queryForRequest,
        // INITIAL_FACET_OVERRIDES first so any caller-supplied facetOverrides
        // win on the same key -- View More refetches via
        // `refetchWithFacetOverrides` carry their own size=100/500 for the
        // expanded facet and must override the initial size=6/7.
        facetOverrides: { ...INITIAL_FACET_OVERRIDES, ...searchQuery.facetOverrides },
        options: getAugmentedSearchOptions(searchQuery.options, searchExclusions, excludedAuctionHouseIds),
    };

    augmentedSearchQuery.topRated = augmentedSearchQuery.options.topRated;

    return augmentedSearchQuery;
};

export function getSearchCompletedTrigger(
    trigger: string | undefined,
    searchSuggestionUsed: boolean,
    submittedFromSearchBar: boolean
): SearchCompletedTrigger {
    if (trigger === SearchUrlTrigger.UpcomingItems) {
        return SearchCompletedTrigger.UpcomingItems;
    }
    // Ahead of the search bar check: the bar keeps showing what the person typed, so the
    // "Search instead for" follow-up would otherwise count as a search-bar submit.
    if (trigger === SearchUrlTrigger.SpellCorrection) {
        return SearchCompletedTrigger.SpellCorrection;
    }
    if (searchSuggestionUsed || submittedFromSearchBar) {
        return SearchCompletedTrigger.SearchBar;
    }
    return SearchCompletedTrigger.Other;
}

type GetSpellCorrectionAnalyticsParams = {
    /**
     * The URL's `originalKeyword`: set once the page has swapped the URL to the corrected keyword.
     */
    originalKeyword?: string;
    spellCorrection?: SpellCorrection;
    trigger?: string;
};

/**
 * The Search Completed properties describing the Did You Mean correction behind the results.
 * A URL already carrying `originalKeyword` (e.g. going Back to a corrected search) is not
 * corrected again, so search-party sends no `spellCorrection` and the distance and dictionary
 * version are unknown.
 */
export const getSpellCorrectionAnalytics = ({
    originalKeyword,
    spellCorrection,
    trigger,
}: GetSpellCorrectionAnalyticsParams): SpellCorrectionAnalytics => {
    const eligible: SpellCorrectionAnalytics = spellCorrection?.suggestion ? { spellCorrectionEligible: true } : {};

    if (spellCorrection?.isCorrected) {
        return {
            ...eligible,
            originalSearchQuery: spellCorrection.originalQuery,
            spellCorrectionApplied: true,
            spellCorrectionDictionaryVersion: spellCorrection.dictionaryVersion,
            spellCorrectionDistance: spellCorrection.distance,
        };
    }
    // Eligible too: it was corrected, and the correction rate needs every applied search to be eligible.
    if (originalKeyword) {
        return { originalSearchQuery: originalKeyword, spellCorrectionApplied: true, spellCorrectionEligible: true };
    }
    if (spellCorrection || trigger === SearchUrlTrigger.SpellCorrection) {
        return { ...eligible, spellCorrectionApplied: false };
    }
    return {};
};
